import { useState, useEffect } from 'react';
import { Team, MatchType, ValidationError } from '@/types';
import { scheduleMatch, updateMatch } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';

export interface ScheduledMatchData {
  id?: string;
  matchNumber: number;
  date: string;
  matchTime: string | null;
  matchType: MatchType;
  teamAId: string;
  teamBId: string;
  liveStreamUrl?: string | null;
}

interface ScheduleMatchModalProps {
  tournamentId: string;
  teams: Team[];
  editingMatch?: ScheduledMatchData | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function ScheduleMatchModal({ tournamentId, teams, editingMatch, onSuccess, onCancel }: ScheduleMatchModalProps) {
  const [matchNumber, setMatchNumber] = useState<number>(editingMatch?.matchNumber ?? 1);
  const [date, setDate] = useState<string>(editingMatch?.date || new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState<string>(editingMatch?.matchTime || '10:00');
  const [matchType, setMatchType] = useState<MatchType>(editingMatch?.matchType || 'league');
  const [teamAId, setTeamAId] = useState<string>(editingMatch?.teamAId || '');
  const [teamBId, setTeamBId] = useState<string>(editingMatch?.teamBId || '');
  const [liveStreamUrl, setLiveStreamUrl] = useState<string>(editingMatch?.liveStreamUrl ?? '');
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!editingMatch?.id;

  useEffect(() => {
    if (teamAId && teamAId === teamBId) {
      setTeamBId('');
    }
  }, [teamAId, teamBId]);

  const validate = (): ValidationError[] => {
    const errs: ValidationError[] = [];

    if (!matchNumber || matchNumber <= 0) {
      errs.push({ id: uuidv4(), title: 'Invalid Match Number', message: 'Match number must be a positive number.' });
    }

    if (!date) {
      errs.push({ id: uuidv4(), title: 'Date Required', message: 'Match date is required.' });
    }

    if (!teamAId) {
      errs.push({ id: uuidv4(), title: 'Team A Required', message: 'Select Team A.' });
    }

    if (!teamBId) {
      errs.push({ id: uuidv4(), title: 'Team B Required', message: 'Select Team B.' });
    }

    if (teamAId && teamBId && teamAId === teamBId) {
      errs.push({ id: uuidv4(), title: 'Duplicate Team', message: 'Team A and Team B must be different.' });
    }

    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      if (isEditing && editingMatch?.id) {
        await updateMatch(editingMatch.id, {
          matchNumber,
          date,
          matchType,
          teamAId,
          teamBId,
          matchTime: time,
          liveStreamUrl,
        });
      } else {
        await scheduleMatch({
          matchNumber,
          date,
          matchType,
          teamAId,
          teamBId,
          tournamentId,
          matchTime: time,
          liveStreamUrl,
        });
      }
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save match.';
      setErrors([{ id: uuidv4(), title: 'Save Failed', message }]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-surface rounded-xl p-6 max-w-md w-full my-8 shadow-2xl">
        <h2 className="text-lg font-bold text-text-primary mb-1">
          {isEditing ? 'Edit Scheduled Match' : 'Schedule Match'}
        </h2>
        <p className="text-xs text-text-muted mb-5">
          {isEditing ? 'Update match details.' : 'Match will be created with Scheduled status.'}
        </p>

        {errors.length > 0 && (
          <div className="mb-4 space-y-1.5">
            {errors.map(err => (
              <div key={err.id} className="bg-error/15 border border-error/30 rounded-lg p-2.5">
                <p className="text-error text-xs font-bold">{err.title}</p>
                <p className="text-error text-xs mt-0.5">{err.message}</p>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Match Number */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">Match Number</label>
            <input
              type="number"
              min={1}
              value={matchNumber}
              onChange={(e) => setMatchNumber(parseInt(e.target.value) || 0)}
              className="input-field text-lg w-full"
              placeholder="1"
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-2">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="input-field text-lg w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="input-field text-lg w-full"
              />
            </div>
          </div>

          {/* Live Streaming URL */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">Live Streaming URL</label>
            <input
              type="url"
              value={liveStreamUrl}
              onChange={(e) => setLiveStreamUrl(e.target.value)}
              placeholder="Paste Live Streaming URL (Optional)"
              className="input-field text-lg w-full"
            />
          </div>

          {/* Match Type */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">Match Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMatchType('league')}
                className={`py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${
                  matchType === 'league'
                    ? 'bg-success text-white'
                    : 'bg-surface-raised text-text-primary border border-border'
                }`}
              >
                League
              </button>
              <button
                type="button"
                onClick={() => setMatchType('playoff')}
                className={`py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${
                  matchType === 'playoff'
                    ? 'bg-success text-white'
                    : 'bg-surface-raised text-text-primary border border-border'
                }`}
              >
                Playoff
              </button>
            </div>
          </div>

          {/* Team A */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">Team A</label>
            <select
              value={teamAId}
              onChange={(e) => setTeamAId(e.target.value)}
              className="input-field text-lg w-full"
            >
              <option value="">Select Team A</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>

          {/* Team B */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">Team B</label>
            <select
              value={teamBId}
              onChange={(e) => setTeamBId(e.target.value)}
              className="input-field text-lg w-full"
            >
              <option value="">Select Team B</option>
              {teams.map(team => (
                <option key={team.id} value={team.id} disabled={team.id === teamAId}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-success text-white py-3 rounded-lg font-semibold disabled:opacity-50"
            >
              {submitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Schedule Match'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
